import { FC, useCallback, useEffect, useState } from 'react';
import React from 'react';
import uniqueId from 'lodash-es/uniqueId';
import isEqual from 'react-fast-compare';
import { dayjs } from '../../utils';
import { useDeepCompare } from '../../utils/useCompare';
import { DatePicker } from '../DatePicker/DatePicker';
import { DateTimePicker } from '../DateTimePicker/DateTimePicker';
import { FormLabel } from '../Form/FormLabel';
import { getHelperText } from '../Form/HelperText';
import { FormControl } from '../Form/FormControl';
import { Select } from '../Select';
import { Input } from '../Form/Input';
import { Popper } from '../Popper/Popper';
import {
  DateRelativePickerModel,
  DateRelativePickerProps,
  DateTypeModel,
  TimeUnitModel
} from './DateRelativePicker.types';
import { DATE_RELATIVE_OPTIONS, DEFAULT_TYPE, TIME_UNIT_OPTIONS } from './DateRelativePicker.constants';
import {
  getMaxFromDate,
  getMinToDate,
  hasClearedValue,
  mergeDateWithTime,
  removeLeadingZero,
  useAmountFieldsHelperText,
  useFromToHelperText,
  useTimeUnitFieldHelperText
} from './DateRelativePicker.utils';
import { dateToIsoDateFormat, dateToShortIsoTimeFormat, isValidTime } from '../../utils/dateUtil';
import { FormHelperText } from '../Form/FormHelperText';
import { styled } from '../../styles';

const DESKTOP_MODE_MEDIA_QUERY = process.env.NODE_ENV !== 'test' ? '@media (min-width: 500px)' : undefined;

export const DateRelativePicker: FC<DateRelativePickerProps> = ({
  helperText,
  error,
  label = 'Relative Date',
  id,
  fullWidth,
  required,
  isOnDatePickerProps,
  isInBetweenFromDatePickerProps,
  isInBetweenToDatePickerProps,
  isInTheNextOcInputProps,
  isInTheLastOcInputProps,
  isWithinThisMuiSelectProps,
  errorText,
  dataTest = 'date-relative-picker',
  value,
  excludedTypes,
  updateOnlyWithValue = true,
  onChange,
  dateTime,
  sx,
  style,
  className
}) => {
  const newId = id ?? uniqueId('DateRelativePicker') + Date.now();
  const { amountFieldErrorMessage, setAmountFieldErrorMessage, getAmountFieldHelperText } = useAmountFieldsHelperText();
  const { getTimeUnitFieldHelperText } = useTimeUnitFieldHelperText();
  const { getFromToHelperText } = useFromToHelperText();
  const [model, setModel] = useState<DateRelativePickerModel | undefined>(value ? value : { type: DEFAULT_TYPE });

  const DateTimeComponent = dateTime ? DateTimePicker : DatePicker;

  const valueHasChanged = useDeepCompare(value);
  const valueIsDiff = valueHasChanged && !isEqual(model, value);
  useEffect(() => {
    if (valueIsDiff) {
      // keep current type if value is undefined to prevent the picker type on clear action
      setModel((prevState) => ({
        ...(value || {}),
        type: prevState?.type ? prevState?.type : DEFAULT_TYPE
      }));
    }
  }, [value, valueIsDiff]);

  const handleChange = useCallback(
    (value: Partial<DateRelativePickerModel>) => {
      const newModel = { ...model, ...value };
      setModel(newModel);
      // update only with FULL value required per type
      if (updateOnlyWithValue) {
        if (newModel.type && hasClearedValue(newModel, model)) {
          onChange(undefined);
        }
        if (newModel.type === 'IS_ON' && newModel.from) {
          onChange({ type: newModel.type, from: newModel.from });
        }
        if (newModel.type === 'IS_IN_BETWEEN' && (newModel.from || newModel.to)) {
          onChange({
            type: newModel.type,
            from: newModel.from,
            timeFrom: newModel?.timeFrom,
            to: newModel.to,
            timeTo: newModel?.timeTo
          });
        }

        if (newModel.type === 'IS_IN_THE_NEXT' && newModel.amount && newModel.amount > 0) {
          onChange({ type: newModel.type, amount: newModel.amount, timeUnit: 'DAY' });
        }

        if (newModel.type === 'IS_IN_THE_LAST' && newModel.amount && newModel.amount > 0) {
          onChange({ type: newModel.type, amount: newModel.amount, timeUnit: 'DAY' });
        }

        if (newModel.type === 'IS_WITHIN_THIS' && newModel.timeUnit) {
          onChange({ type: newModel.type, timeUnit: newModel.timeUnit });
        }

        if (newModel.type === 'IS_EMPTY') {
          onChange({ type: newModel.type });
        }
      } else {
        onChange(newModel);
      }
    },
    [model, onChange, updateOnlyWithValue]
  );

  return (
    <FormControl
      dataTest={dataTest}
      required={required}
      error={error}
      fullWidth={fullWidth}
      sx={sx}
      style={style}
      className={className}
    >
      <StyledTopSection>
        <FormLabel dataTest={`${dataTest}_label`} htmlFor={newId} required={required}>
          {label}
        </FormLabel>
        <Select<DateTypeModel>
          className="small-select"
          // listBoxProps and Popper component are used to set the width of the dropdown
          // the width of the dropdown is different from the width of the input field
          listBoxProps={{ width: '130px' }}
          components={{
            Popper: (props) => <Popper children={undefined} {...props} style={{ ...props.style, width: '130px' }} />
          }}
          dataTest={`${dataTest}_type`}
          value={model?.type}
          disableClearable
          fullWidth={false}
          options={DATE_RELATIVE_OPTIONS.filter((option) => {
            if (!excludedTypes?.length) {
              return true;
            }
            return !excludedTypes.includes(option.value);
          })}
          onChange={(type) => handleChange({ type })}
        />
      </StyledTopSection>
      <div>
        {model?.type === 'IS_ON' && (
          <DatePicker
            placeholder="From"
            {...isOnDatePickerProps}
            fullWidth={fullWidth}
            clearable
            desktopModeMediaQuery={DESKTOP_MODE_MEDIA_QUERY}
            dataTest={`${dataTest}_is-on`}
            value={model?.from ? dayjs(model.from) : null}
            onChange={(from) => handleChange({ from: dateToIsoDateFormat(from?.toDate()) || undefined })}
          />
        )}
        {model?.type === 'IS_IN_BETWEEN' && (
          <StyledIsInBetweenWrapper>
            <DateTimeComponent
              placeholder="From"
              clearable
              {...isInBetweenFromDatePickerProps}
              fullWidth
              desktopModeMediaQuery={DESKTOP_MODE_MEDIA_QUERY}
              maxDate={getMaxFromDate(model?.to, isInBetweenFromDatePickerProps?.maxDate)}
              dataTest={`${dataTest}_is-in-between-from`}
              value={toDayjs(dateTime ? mergeDateWithTime(model.from, model.timeFrom) : model?.from)}
              onChange={(from) => {
                const nextModel = {
                  from: dateToIsoDateFormat(from?.toDate()) || undefined
                };
                if (dateTime) {
                  const nextTime = from ? dateToShortIsoTimeFormat(from?.toDate()) : undefined;
                  handleChange({ ...nextModel, timeFrom: nextTime && isValidTime(nextTime) ? nextTime : undefined });
                } else {
                  handleChange(nextModel);
                }
              }}
            />
            <DateTimeComponent
              placeholder="To"
              {...isInBetweenToDatePickerProps}
              clearable
              fullWidth
              desktopModeMediaQuery={DESKTOP_MODE_MEDIA_QUERY}
              minDate={getMinToDate(model?.from, isInBetweenToDatePickerProps?.minDate)}
              dataTest={`${dataTest}_is-in-between-to`}
              value={toDayjs(dateTime ? mergeDateWithTime(model.to, model.timeTo) : model?.to)}
              onChange={(to) => {
                const nextModel: DateRelativePickerModel = { to: dateToIsoDateFormat(to?.toDate()) || undefined };
                if (dateTime) {
                  const nextTime = to ? dateToShortIsoTimeFormat(to?.toDate()) : undefined;
                  handleChange({ ...nextModel, timeTo: nextTime && isValidTime(nextTime) ? nextTime : undefined });
                } else {
                  handleChange(nextModel);
                }
              }}
            />
            {(!!model.to || !!model.from) && (
              <FormHelperText className="helper-text">
                {getFromToHelperText(model.from, model.to, model.timeFrom, model.timeTo)}
              </FormHelperText>
            )}
          </StyledIsInBetweenWrapper>
        )}
        {model?.type === 'IS_IN_THE_NEXT' && (
          <>
            <StyledNumberInput
              {...isInTheNextOcInputProps}
              error={!!amountFieldErrorMessage}
              endAdornment={<StyledIsInTheNextDays>Days</StyledIsInTheNextDays>}
              $fullWidth={!!fullWidth}
              $valueIsO={model?.amount === 0 || !model?.amount}
              dataTest={`${dataTest}_is-in-the-next`}
              onChange={(e) => {
                let number = e.target.value ? +removeLeadingZero(e.target.value) : 0;
                if (number <= -1) {
                  setAmountFieldErrorMessage('Minimum of 1 day');
                } else if (number > 9999) {
                  number = 9999;
                  setAmountFieldErrorMessage('Maximum of 9999 day');
                } else {
                  setAmountFieldErrorMessage('');
                }
                handleChange({ amount: number ?? 0 });
              }}
              value={model?.amount?.toString() ?? 0}
              type="number"
            />
            <FormHelperText className="helper-text" sx={{ mt: 4 }}>
              {getAmountFieldHelperText(model?.amount, 'IS_IN_THE_NEXT')}
            </FormHelperText>
          </>
        )}
        {model?.type === 'IS_IN_THE_LAST' && (
          <>
            <StyledNumberInput
              {...isInTheLastOcInputProps}
              error={!!amountFieldErrorMessage}
              $valueIsO={model?.amount === 0 || !model?.amount}
              endAdornment={<StyledIsInTheNextDays>Days</StyledIsInTheNextDays>}
              $fullWidth={!!fullWidth}
              dataTest={`${dataTest}_is-in-the-last`}
              onChange={(e) => {
                let number = e.target.value ? +removeLeadingZero(e.target.value) : 0;
                if (number <= 0) {
                  setAmountFieldErrorMessage('Minimum of 1 day');
                } else if (number > 9999) {
                  number = 9999;
                  setAmountFieldErrorMessage('Maximum of 9999 day');
                } else {
                  setAmountFieldErrorMessage('');
                }
                handleChange({ amount: number ?? 0 });
              }}
              value={model?.amount?.toString() ?? 0}
              type="number"
            />
            <FormHelperText className="helper-text" sx={{ mt: 4 }}>
              {getAmountFieldHelperText(model?.amount, 'IS_IN_THE_LAST')}
            </FormHelperText>
          </>
        )}
        {model?.type === 'IS_WITHIN_THIS' && (
          <>
            <Select<TimeUnitModel>
              {...isWithinThisMuiSelectProps}
              dataTest={`${dataTest}_is-within-this`}
              options={TIME_UNIT_OPTIONS}
              onChange={(timeUnit) => handleChange({ timeUnit })}
              value={model?.timeUnit}
              style={!fullWidth ? { width: '200px' } : undefined}
              fullWidth={fullWidth}
            />
            <FormHelperText className="helper-text" sx={{ mt: 3 }}>
              {getTimeUnitFieldHelperText(model?.timeUnit)}
            </FormHelperText>
          </>
        )}
      </div>
      {(!!helperText || !!errorText) && (
        <FormHelperText dataTest={`${dataTest}_helper-text`} error={error}>
          {getHelperText(error, helperText, errorText)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

function toDayjs(date: string | undefined): dayjs.Dayjs | null {
  if (!date) return null;
  return dayjs(date);
}

const StyledIsInTheNextDays = styled('div')`
  position: absolute;
  right: -40px;
`;

const StyledNumberInput = styled(Input)<{ $fullWidth: boolean; $valueIsO: boolean }>`
  width: ${({ $fullWidth }) => ($fullWidth ? 'calc(100% - 40px)' : '130px')};
  .MuiInputBase-input {
    // This is workaround for the input value to be not visible when it is 0
    // We cannot set undefined to resolve this issue because it will throw an error that we change from controlled to uncontrolled input
    // We don't display 0 to avoid confusion with empty value
    color: ${({ $valueIsO }) => ($valueIsO ? 'transparent' : 'inherit')};
  }
`;

const StyledIsInBetweenWrapper = styled('div')`
  display: grid;
  width: 100%;
  gap: ${({ theme }) => theme.spacingSize.m};
  grid-template-columns: 1fr 1fr;
  
  .MuiCollapse-root:has(.helper-text) {
    grid-column: span 2;
  }
`;

const StyledTopSection = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  
  .MuiInputLabel-root {
    position: relative;
    margin-bottom: ${({ theme }) => theme.spacingSize.s};
  }
  .MuiAutocomplete-root {
    margin-top: 0 !important;
    margin-left: 4px;
    background: transparent;
    top: -2px;
    position: relative;
    
    .MuiInputBase-root,
    .MuiAutocomplete-input {
      min-height: 0;
      background: transparent;
      border-color: transparent;
      
      &:hover {
        box-shadow: none;
        background-color: rgba(26, 26, 26, 0.08);
      }
    }
  }
`;
